import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { cyan, border, card, green, amber, red, todayKey, money } from "./fieldUi";
import { attentionItems, serviceKpis } from "../utils/dashboardAttention";
import { formatShortDate } from "../utils/dates";
import { formatWeather } from "../utils/weatherText";

// The top of the dashboard for rig services companies
// (GET /api/reports/service-dashboard): 4 cards, "Needs attention today"
// (at most 5, most urgent first), and one compact row per rig. Everything
// else lives on its own page (links at the bottom).
const STATE_COLOR = { working: green, idle: "rgba(255,255,255,0.65)", down: red };
const SHEET_COLOR = { draft: "rgba(255,255,255,0.7)", submitted: amber, approved: green, sent: green, none: amber };
const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };
const badge = (color) => ({ flex: "none", border: `1px solid ${color}`, color, borderRadius: "10px", padding: "1px 8px", fontSize: "12px", fontWeight: 600, lineHeight: "18px", whiteSpace: "nowrap" });
const ROWS = 10;

export default function ServiceDashboardPanel({ token, user, data, onOpenReports, onOpenJobs, onOpenCerts, onNewReport, onOpenRig, onOpenRigs, onOpenCalendar, onOpenCustomers, onOpenReport, crewView = false }) {
  const { t, i18n } = useTranslation();
  const [fetched, setFetched] = useState(null);
  const [error, setError] = useState("");
  const [allRigs, setAllRigs] = useState(false);
  // "Tour sheets missing yesterday" opens the list of those rigs right here.
  const [showMissing, setShowMissing] = useState(false);

  // The Dashboard usually passes the data in (it also needs it); fetch only
  // when used on its own.
  useEffect(() => {
    if (data !== undefined) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/reports/service-dashboard`, { headers: { Authorization: "Bearer " + token }, params: { today: todayKey(), ...(crewView ? { scope: "mine" } : {}) }, timeout: 20000 })
      .then((r) => setFetched(r.data || {}))
      .catch((err) => setError(err.response?.data?.error || t("serviceDash.loadFailed")));
  }, [token, t, data, crewView]);

  const d = data || fetched;
  if (error) return <div style={{ color: "#ff8899", marginBottom: "20px" }}>{error}</div>;
  if (!d) return <div style={{ color: "rgba(0,245,255,0.5)", marginBottom: "20px" }}>{t("rigReport.loading")}</div>;

  const isManager = ["admin", "supervisor"].includes(user.role);
  const k = serviceKpis(d);
  const items = attentionItems(d, { isManager });
  const rigs = d.rigs || [];
  const lang = i18n.language;

  const open = (target) => {
    if (target.type === "rig" && onOpenRig) onOpenRig(target.rigId);
    else if (target.type === "newReport" && onNewReport) onNewReport(target.rigId, target.dateKey);
    else if (target.type === "certs" && onOpenCerts) onOpenCerts();
    else if (target.type === "job" && onOpenJobs) onOpenJobs();
    else if (target.type === "report" && onOpenReports) onOpenReports();
  };

  const kpi = (testId, value, label, color, onClick, sub) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{ background: card, border: `1px solid ${border}`, borderRadius: "10px", padding: "14px", textAlign: "left", cursor: onClick ? "pointer" : "default", color: "white", fontFamily: "inherit", minHeight: "92px" }}
    >
      <div style={{ fontSize: "30px", fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}
        {sub && <span style={{ fontSize: "15px", color: "rgba(255,255,255,0.55)", fontWeight: 600 }}> {sub}</span>}
      </div>
      <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.8)", marginTop: "6px" }}>{label}</div>
    </button>
  );
  const h3 = { color: "white", fontSize: "17px", fontWeight: 700, margin: "22px 0 10px" };
  const row = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "10px 0", borderBottom: `1px solid ${border}`, fontSize: "14px", color: "rgba(255,255,255,0.88)", flexWrap: "wrap" };

  const itemText = (it) => {
    const x = it.data;
    const where = [x.job?.customerName || x.customerName, x.job?.wellName || x.wellName].filter(Boolean).join(" — ");
    switch (it.kind) {
      case "rigDown":
        return t("serviceDash.att.rigDown", { rig: x.name });
      case "missingSheet":
        return t("serviceDash.att.missingSheet", { rig: x.name, date: formatShortDate(x.dateKey, lang), where: where ? ` · ${where}` : "" });
      case "certExpired":
        return t("serviceDash.att.certExpired", { name: x.userName, cert: t(`certTypes.${x.certificationType}`, { defaultValue: x.certificationType }) });
      case "certExpiring":
        return t("serviceDash.att.certExpiring", { name: x.userName, cert: t(`certTypes.${x.certificationType}`, { defaultValue: x.certificationType }), count: x.daysLeft });
      case "afeOver":
      case "afeNear":
        // (a job title already starts with the customer: "Customer — Well" — no second name)
        return t(`serviceDash.att.${it.kind}`, { job: x.title && x.customerName && String(x.title).startsWith(x.customerName) ? x.title : [x.customerName, x.title].filter(Boolean).join(" — "), pct: Math.round(x.pctSpent || 0), cost: money(x.costToDate, lang), budget: money(x.approvedAmount, lang) });
      case "awaitingOld":
      case "awaiting":
        return t("serviceDash.att.awaiting", { rig: x.rigName, date: formatShortDate(x.dateKey, lang), count: x.age });
      default:
        return "";
    }
  };
  const itemColor = (it) => (it.urgency >= 80 ? red : it.urgency >= 50 ? amber : "rgba(255,255,255,0.75)");

  const shown = allRigs ? rigs : rigs.slice(0, ROWS);
  const link = (label, onClick, testId) =>
    onClick ? (
      <button key={testId} type="button" data-testid={testId} onClick={onClick} style={{ background: "none", border: `1px solid ${border}`, color: cyan, borderRadius: "8px", padding: "10px 14px", fontSize: "14px", cursor: "pointer", minHeight: "44px" }}>
        {label} →
      </button>
    ) : null;

  return (
    <section data-testid="service-dashboard" style={{ marginBottom: "28px" }}>
      {/* (a toolpusher's / crew's home: their rig's items only — no company tiles or links) */}
      {!crewView && (
      <div data-testid="sd-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
        {kpi("sd-working", k.working, t("serviceDash.kpi.working"), green, onOpenRigs, t("serviceDash.kpi.ofRigs", { n: k.rigs }))}
        {kpi("sd-missing", k.missingYesterday, t("serviceDash.kpi.missing"), k.missingYesterday ? red : green, (d.missingYesterday || []).length ? () => setShowMissing(!showMissing) : undefined)}
        {kpi("sd-awaiting", k.awaitingApproval, t("serviceDash.kpi.awaiting"), k.awaitingApproval ? amber : green, onOpenReports)}
        {kpi("sd-certs", k.certIssues, t("serviceDash.kpi.certs"), k.certIssues ? amber : green, onOpenCerts)}
      </div>
      )}

      {showMissing && (
        <div data-testid="sd-missing-list" style={{ marginTop: "12px", border: `1px solid ${border}`, borderRadius: "10px", padding: "4px 12px" }}>
          <div style={{ ...row, color: "rgba(255,255,255,0.6)", fontSize: "13px", borderBottom: "none" }}>{t("serviceDash.missingHelp")}</div>
          {(d.missingYesterday || []).map((m) => (
            <button key={m.rigId} type="button" data-testid="sd-missing-row" disabled={!onNewReport} onClick={() => onNewReport && onNewReport(m.rigId, m.dateKey)} style={{ ...row, width: "100%", background: "none", border: "none", borderBottom: `1px solid ${border}`, textAlign: "left", cursor: onNewReport ? "pointer" : "default", fontFamily: "inherit" }}>
              <span style={{ minWidth: 0 }}>
                <strong style={{ color: "white" }}>{m.name}</strong> · {formatShortDate(m.dateKey, lang)}
                {m.job?.wellName ? <span style={{ color: "rgba(255,255,255,0.55)" }}> · {[m.job.customerName, m.job.wellName].filter(Boolean).join(" — ")}</span> : null}
              </span>
              {onNewReport && <span style={{ color: cyan, flex: "none" }}>{t("serviceDash.writeIt")} ›</span>}
            </button>
          ))}
        </div>
      )}

      <h3 style={h3}>{t("serviceDash.attention")}</h3>
      <div data-testid="sd-attention">
        {items.length === 0 ? (
          <div data-testid="sd-all-clear" style={{ ...row, color: green }}>
            ✓ {t("serviceDash.allClear")}
          </div>
        ) : (
          items.map((it) => (
            <button key={it.key} type="button" data-testid="sd-attention-item" onClick={() => open(it.target)} style={{ ...row, flexWrap: "nowrap", width: "100%", background: "none", border: "none", borderBottom: `1px solid ${border}`, textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
              <span style={{ display: "flex", gap: "10px", alignItems: "center", flex: 1, minWidth: 0 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: itemColor(it), flex: "none" }} />
                <span style={{ minWidth: 0 }}>{itemText(it)}</span>
              </span>
              <span data-testid="sd-chevron" style={{ color: cyan, flex: "none", fontSize: "18px" }}>
                ›
              </span>
            </button>
          ))
        )}
      </div>

      <h3 style={h3}>{t("serviceDash.rigsToday")}</h3>
      <div data-testid="sd-rigs">
        {rigs.length === 0 && <div style={{ ...row, color: "rgba(255,255,255,0.5)" }}>{t("serviceDash.noRigs")}</div>}
        {shown.map((r) => {
          const well = r.currentWell?.wellName || r.job?.wellName || "";
          const onJob = r.state === "working" || (r.state === "down" && well);
          const where = onJob ? [r.currentWell?.customerName || r.job?.customerName, well].filter(Boolean).join(" — ") : "";
          const sheetKey = r.todayReport ? r.todayReport.status : r.state === "working" ? "none" : "";
          const sheet = r.todayReport ? t(`rigReport.status.${r.todayReport.status}`) : sheetKey === "none" ? t("serviceDash.noSheetYet") : "";
          const wx = r.state === "working" ? formatWeather(r.weather, t) : "";
          // Short badges; a down rig on a job says where on the second line.
          const stateLabel = r.state === "down" ? t("serviceDash.state.downShort") : t(`serviceDash.state.${r.state}`);
          const line2 = r.state === "working" ? where : r.state === "down" && well ? t("serviceDash.state.downOn", { well }) : "";
          return (
            <div key={r.rigId} style={{ display: "flex", alignItems: "stretch", borderBottom: `1px solid ${border}` }}>
            <button type="button" data-testid="sd-rig-row" onClick={() => (r.todayReport && onOpenReport ? onOpenReport(r.todayReport._id) : onOpenRig && onOpenRig(r.rigId))} style={{ display: "block", flex: 1, minWidth: 0, background: "none", border: "none", padding: "7px 0", textAlign: "left", cursor: onOpenRig || onOpenReport ? "pointer" : "default", fontFamily: "inherit", color: "rgba(255,255,255,0.88)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                <strong data-testid="sd-rig-name" style={{ ...ellipsis, color: "white", fontSize: "14px", flex: "0 1 auto" }}>{r.name}</strong>
                <span data-testid="sd-rig-state" style={badge(STATE_COLOR[r.state] || "white")}>
                  {stateLabel}
                </span>
                <span style={{ flex: 1 }} />
                {sheet && (
                  <span data-testid="sd-rig-sheet" style={badge(SHEET_COLOR[sheetKey] || "white")}>
                    {sheet}
                  </span>
                )}
              </span>
              {line2 || wx ? (
                <span data-testid="sd-rig-line2" style={{ ...ellipsis, display: "block", color: "rgba(255,255,255,0.55)", fontSize: "12px", marginTop: "2px" }}>
                  {line2}
                  {line2 && wx ? " · " : ""}
                  {wx && <span data-testid="sd-weather">{wx}</span>}
                </span>
              ) : null}
            </button>
            {r.todayReport && onOpenReport && onOpenRig && (
              <button type="button" data-testid="sd-rig-details" aria-label={t("serviceDash.rigDetails", { rig: r.name })} onClick={() => onOpenRig(r.rigId)} style={{ flex: "none", background: "none", border: "none", color: cyan, fontSize: "13px", padding: "0 0 0 10px", cursor: "pointer", fontFamily: "inherit" }}>
                {t("serviceDash.rigShort")} ›
              </button>
            )}
            </div>
          );
        })}
        {rigs.length > ROWS && (
          <button type="button" data-testid="sd-rigs-all" onClick={() => setAllRigs(!allRigs)} style={{ background: "none", border: "none", color: cyan, fontSize: "14px", padding: "10px 0", cursor: "pointer" }}>
            {allRigs ? t("serviceDash.showFewer") : t("serviceDash.showAll", { n: rigs.length })}
          </button>
        )}
      </div>

      {!crewView && (
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "18px" }}>
        {link(t("serviceDash.links.reports"), onOpenReports, "sd-link-reports")}
        {link(t("serviceDash.links.jobs"), onOpenJobs, "sd-link-jobs")}
        {link(t("serviceDash.links.rigs"), onOpenRigs, "sd-link-rigs")}
        {link(t("serviceDash.links.customers"), onOpenCustomers, "sd-link-customers")}
        {link(t("serviceDash.links.calendar"), onOpenCalendar, "sd-link-calendar")}
      </div>
      )}
    </section>
  );
}
